import {
  forwardRef,
  ReactElement,
  Ref,
  SyntheticEvent,
  useId,
  useMemo
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cc from 'classcat';
import { convert } from 'utils/betslip/odd-formats-helper';
import { EIconSize } from 'utils/constants/app/ui';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { fireEvent } from 'utils/generic/event-bus';
import { isMobile } from 'utils/is-mobile';
import { calculateBoostedOdds } from 'utils/sportsbook/calculate-boosted-odds';
import { getIndicatorPosition } from 'utils/sportsbook/get-inidcator-position';
import { FULL_WIDTH_PERCENTAGE } from './index';
import { GlobalIcon } from 'components/GlobalIcon';
import { ERecaptchaEventBusActionTypes } from 'components/Recaptcha/types';
import { OddIndicator } from 'newcomponents/Shared/sportsbook/OddIndicator';
import { usePrevious } from 'hooks/usePrevious';
import { useEventbus } from 'newhooks/generic/useEventbus';
import { setQuickBetSelectedEventId } from 'store/actions';
import { EBoostType } from 'store/reducers/sport-data';
import { getRecaptchaEnabled } from 'store/selectors/app-data';
import {
  getQuickBetEnabled,
  getQuickBetSelectedEventId,
  getQuickBetStake,
  getShouldShowQuickBetInfoPopover
} from 'store/selectors/betslip';
import { getBoostedOdds } from 'store/selectors/sport-data';
import type { TOddProps } from './Odd';

export const OddButton = forwardRef(
  (
    {
      size,
      visible,
      parent,
      handicap,
      name,
      value,
      style,
      isBlocked,
      fullName,
      marketType,
      type1,
      base,
      gameId,
      noBoosted,
      coeficient,
      oddFormat,
      isLive,
      oddTextColor,
      isUsaSinglePage
    }: TOddProps & { visible: boolean },
    buttonRef
  ): ReactElement => {
    //we cant use eventId as there can be odds with same id in same page (like in classic sportsbook when single view is open and same odd exist in games list)
    const uniqueId = useId();
    const dispatch = useDispatch();

    const quickBetStake = useSelector(getQuickBetStake);
    const quickBetSelectedEventId = useSelector(getQuickBetSelectedEventId);
    const boostedOdds = useSelector(getBoostedOdds);
    const prevCoefficient = usePrevious(coeficient);
    const recaptchaEnabled = useSelector(getRecaptchaEnabled);
    const isQuickBetEnabled = useSelector(getQuickBetEnabled);
    const shouldShowQuickBetInfoPopover = useSelector(
      getShouldShowQuickBetInfoPopover
    );

    const isOddActive =
      parent.value.includes(value) || uniqueId === quickBetSelectedEventId;

    const handleClickRecaptchaWrapper = (event: SyntheticEvent) => {
      event?.stopPropagation();

      if (!coeficient || coeficient === Infinity) {
        return;
      }

      if (isBlocked) {
        return;
      }

      if (isQuickBetEnabled && quickBetStake) {
        dispatch(setQuickBetSelectedEventId(uniqueId));
      }

      if (
        isQuickBetEnabled &&
        !shouldShowQuickBetInfoPopover &&
        recaptchaEnabled
      ) {
        fireEvent({
          key: ERecaptchaEventBusActionTypes.Validate,
          payload: uniqueId
        });

        return;
      }

      handleClick();
    };

    const handleClick = (): void => {
      if (isOddActive) {
        parent.onChange(value);
      } else {
        parent.onChange(value, coeficient);
      }
    };

    useEventbus(
      {
        [ERecaptchaEventBusActionTypes.Verified]: ({
          payload
        }: {
          payload: string;
        }) => {
          if (payload === uniqueId) {
            handleClick();
          }
        }
      },
      [parent.onChange]
    );

    const isBoostedOdd =
      !noBoosted &&
      boostedOdds?.[gameId]?.[value] &&
      (boostedOdds?.[gameId]?.[value].type === EBoostType.ALL ||
        boostedOdds?.[gameId]?.[value].type ===
          (isLive ? EBoostType.LIVE : EBoostType.PREMATCH));

    const getCanShowHandicaps = () => {
      if (isMobile() || parent.showHandicaps) {
        if (parent.hideHandicapName && typeof base !== 'number') {
          return false;
        }

        return true;
      }

      return false;
    };

    const indicatorValue = useMemo(() => {
      return getIndicatorPosition(prevCoefficient, coeficient);
    }, [coeficient, prevCoefficient]);

    return (
      <span
        data-testid="odd"
        onClick={visible ? parent.hidePopover : handleClickRecaptchaWrapper}
        style={{
          ...{
            minWidth: `${
              Math.floor(FULL_WIDTH_PERCENTAGE / parent.marketColCount) - 2
            }%`
          },
          ...style
        }}
        ref={buttonRef as Ref<HTMLButtonElement>}
        className={cc([
          'xOddButton',
          {
            'xOddButton--colCount': parent.marketColCount,
            'xOddButton--active': isOddActive,
            'xOddButton--justifyBetween':
              !!handicap || (isMobile() && !!name) || getCanShowHandicaps(),
            'xOddButton--disabled': !!isBlocked,
            'xOddButton--large': size === 'large',
            'xOddButton--whiteText': parent.whiteText,
            oddOverflow: isBoostedOdd,
            'xOddButton--defaultHover':
              !isMobile() && !isOddActive && !parent.whiteText && !isBlocked,
            usaOdds: isUsaSinglePage,
            [`x-odd__btn--${indicatorValue}`]: isUsaSinglePage && indicatorValue
          }
        ])}
      >
        {!!handicap && (
          <span
            className={cc([
              'handicap',
              'xOddButton__handicap',
              { globalEllipsis: !fullName },
              { 'xOddButton__handicap-mobile ': isMobile() }
            ])}
          >
            {handicap}
          </span>
        )}
        {getCanShowHandicaps() && (
          <span
            className={cc([
              'handicap',
              'xOddButton__handicap',
              { globalEllipsis: !fullName }
            ])}
          >
            {!parent.hideHandicapName
              ? (marketType && marketType.includes('Handicap')
                  ? type1
                  : name) || ''
              : ''}
            {typeof base === 'number' && ` (${base})`}
          </span>
        )}
        <span
          style={{
            color: oddTextColor
              ? oddTextColor
              : parent.whiteText
              ? 'white'
              : 'text-color'
          }}
          className="xOddButton__coef"
        >
          {isBlocked ? (
            <GlobalIcon
              lib="generic"
              name="lock"
              theme="default"
              size={EIconSize._11}
              className="isBlocked-odd"
            />
          ) : isBoostedOdd ? (
            <div className="boostedOdd">
              <GlobalIcon
                lib="generic"
                name="boostedOdds"
                theme="colored"
                size={EIconSize._14}
                className="boostedOdd--icon"
              />
              <span className="boostedOdd--header">
                {convert(coeficient, oddFormat)}
                {+SpringConfigs.PREVIOUS_BOOSTED_ODDS ? (
                  <span className="boostedOdd__calculation">
                    {convert(calculateBoostedOdds(coeficient), oddFormat)}
                  </span>
                ) : null}
              </span>
            </div>
          ) : (
            convert(coeficient, oddFormat)
          )}
          {prevCoefficient && (
            <OddIndicator
              indicatorValue={indicatorValue}
              isUsaLayout={isUsaSinglePage}
              key={indicatorValue}
            />
          )}
        </span>
      </span>
    );
  }
);
